import { Component, computed, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Services } from "src/app/services/api.service";
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ErrorMessageService } from 'src/app/services/error-message.util';
import { ProfileSidebarComponent } from 'src/app/layout/profile-sidebar/profile-sidebar.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CountriesStore } from 'src/app/services/countries.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalNames } from 'src/app/common/modal.config';
import { ModalService } from 'src/app/services/modal.service';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { Country } from 'src/app/common/countries.model';
import { GetUserProfileService } from 'src/app/services/getUserProfile.service';
import { trimAllControls } from 'src/app/utils/trim-values.util';

@Component({
  selector: 'app-update-profile',
  templateUrl:  `./${environment.pageFolder}/update-profile.${environment.pageFolder}.component.html`,
  styleUrls:  [`./${environment.pageFolder}/update-profile.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [ProfileSidebarComponent, CommonModule, TranslateModule, ReactiveFormsModule, ChangePasswordComponent]
})

export class UpdateProfileComponent implements OnInit {
  successMessage: string = "";
  errorMessage: string = "";
  userEmail: string = "";
  updateProfileTypeList = this.formBuilder.group({
    firstName: ['', [ Validators.required ]],
    lastName: ['', [ Validators.required ]],
    address: ['', [ Validators.required ]],
    houseNo: ['', [ Validators.required ]],
    city: ['', [ Validators.required ]],
    postNo: ['', [ Validators.required ]],
    countryId: ['', [ Validators.required ]],
    phoneNo: ['', [ Validators.required ]]
  });
  countryList: Country[] = [];
  loader = document.getElementById("loader-container");
  page = environment.page;
  private readonly destroyRef = inject(DestroyRef);

  public readonly modalNamesEnum = ModalNames;
  readonly isChangePasswordModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.ChangePassword) ?? false);

  constructor(
    private readonly formBuilder: FormBuilder, 
    public services: Services, 
    private readonly http: HttpClient, 
    private readonly countriesStore: CountriesStore, private readonly errorMessageService: ErrorMessageService,
    public modalService: ModalService,
    private readonly profileStore: GetUserProfileService
  ){
    this.countriesStore.fetchCountries().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.countryList = response;  
    })
  }

  ngOnInit(): void {
    this.loader!.style.display = "none"; 
    this.getUserProfile();
  }

  updateUserProfile() {
    const form = this.updateProfileTypeList;
    trimAllControls(this.updateProfileTypeList);

    if (form.invalid) {
      form.markAllAsTouched();
      this.errorMessage = this.errorMessageService.getErrorMessage('allFieldsRequired');
      return;
    }

    const updatedProfile = {
      firstName: form.value.firstName!,
      lastName: form.value.lastName!,
      address: form.value.address!,
      houseNo: form.value.houseNo!,
      city: form.value.city!,
      postNo: form.value.postNo!,
      countryId: Number(form.value.countryId!),
      phoneNo: form.value.phoneNo!
    };

    this.loader!.style.display = "flex";
    this.errorMessage = "";
    this.successMessage = "";

    this.profileStore.updateUserProfile(updatedProfile).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (latestProfileData) => {
        this.loader!.style.display = "none";
        this.successMessage = this.errorMessageService.getSuccessMessage('profileUpdatedSuccess');
        
        this.updateProfileTypeList.patchValue({
          firstName: latestProfileData.firstName,
          lastName: latestProfileData.lastName,
          address: latestProfileData.address,
          houseNo: latestProfileData.houseNo,
          city: latestProfileData.city,
          postNo: latestProfileData.postNo,
          countryId: latestProfileData.countryId ? latestProfileData.countryId.toString() : '',
          phoneNo: latestProfileData.phoneNo
        });

        this.profileStore.getConnectionUserProfile(true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        setTimeout(() => (this.successMessage = ''), 3000);
      },
      error: () => {
        this.loader!.style.display = "none";
        this.errorMessage = this.errorMessageService.getErrorMessage('error');
      },
    });
  }

  getUserProfile() {
    this.profileStore.getUserProfile().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (userProfile) => {
        this.userEmail = userProfile.email;
        this.updateProfileTypeList.patchValue({
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          address: userProfile.address,
          houseNo: userProfile.houseNo,
          city: userProfile.city,
          postNo: userProfile.postNo,
          countryId: userProfile.countryId ? userProfile.countryId.toString() : '',
          phoneNo: userProfile.phoneNo
        });
      },
      error: (error: HttpErrorResponse) => {
        if(error.status === 401) {
          this.services.logout();
        }
      },
    });
  }

  openChangePassowordModal(){
    this.modalService.openModal(ModalNames.ChangePassword);
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(_: KeyboardEvent) {
    if (this.isChangePasswordModalOpen()) {
      this.modalService.closeModal(ModalNames.ChangePassword);
    }
  }
}
